import { useRef, type ReactNode } from "react";
import {
  CheckCircle2,
  ClipboardList,
  Eye,
  FileSearch,
  GitBranch,
  Lightbulb,
  MessageSquare,
  RefreshCw,
  RotateCw,
  SearchCheck,
  Sparkles,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useScrollSpotlight } from "../scroll-spotlight";

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, description, accentClass }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string; accentClass: string }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06]">
        <Icon className={cn("h-5 w-5", accentClass)} />
      </div>
      <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1 — Review overview                                                  */
/* -------------------------------------------------------------------------- */

export function ReviewOverviewStep() {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="What does reviewing mean?">
        <p className="text-sm leading-7 text-muted-foreground">
          The Orchestrator just finished a bunch of work. Now {"it's"} your turn to look at what
          was done and make sure {"it's"} right. Think of it like checking homework — the work is
          done, you just need to confirm {"it's"} good.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          {"Don't"} worry — you {"don't"} need to read every line of code. SpecTree gives you
          the tools to check things quickly.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="What to look at">
        <p className="text-sm leading-7 text-muted-foreground">
          Open your epic in SpecTree and check these three things:
        </p>
        <div className="grid gap-3 md:grid-cols-3">
          <InfoCard
            icon={FileSearch}
            title="Changed files"
            description="Each feature shows which files were created or modified. Start here to understand what happened."
            accentClass="text-rose-400"
          />
          <InfoCard
            icon={GitBranch}
            title="Decisions log"
            description={"The agent explains why it made certain choices. This helps when something looks unexpected."}
            accentClass="text-rose-400"
          />
          <InfoCard
            icon={SearchCheck}
            title="Acceptance criteria"
            description="Each task has criteria that should be met. Check if the results match what was expected."
            accentClass="text-rose-400"
          />
        </div>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Compare to your original request">
        <p className="text-sm leading-7 text-muted-foreground">
          Go back to the Epic Request you created at the start. Does the finished work match what
          you asked for? {"Here's"} a quick mental checklist:
        </p>
        <div className="space-y-2">
          <ReviewItem>Does it solve the problem you described?</ReviewItem>
          <ReviewItem>Is anything missing from the original scope?</ReviewItem>
          <ReviewItem>Is there anything extra that {"wasn't"} asked for?</ReviewItem>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 2 — Validate the output                                              */
/* -------------------------------------------------------------------------- */

export function ValidateOutputStep() {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Check the automated validations">
        <p className="text-sm leading-7 text-muted-foreground">
          Many tasks have automated checks built in — things like {"\"does the test pass?\""} or
          {"\"does the file exist?\""}. Open each feature in SpecTree and look for the validation
          results. Green checkmarks mean {"it's"} good.
        </p>
        <div className="flex items-start gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] px-4 py-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
          <div>
            <p className="text-sm font-semibold text-foreground">All validations passed?</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Great — the automated part is done. Move on to the human review below.
            </p>
          </div>
        </div>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Do a quick human review">
        <p className="text-sm leading-7 text-muted-foreground">
          Automation catches a lot, but not everything. Give the output a quick once-over with
          fresh eyes:
        </p>
        <div className="space-y-2">
          <ReviewItem>Does the output match what you expected?</ReviewItem>
          <ReviewItem>Are there edge cases or scenarios that might have been missed?</ReviewItem>
          <ReviewItem>Does the code look reasonable at a glance?</ReviewItem>
        </div>
        <p className="mt-2 text-sm leading-7 text-muted-foreground">
          You {"don't"} need to be a code expert. If something feels off, make a note — {"that's"}
          what the next step is for.
        </p>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Record anything that needs follow-up">
        <p className="text-sm leading-7 text-muted-foreground">
          Found something that needs fixing or improving? {"Don't"} try to remember it — create a
          new task or leave a note directly in SpecTree. This way the next session starts with
          clear context instead of guesswork.
        </p>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-500/10 text-rose-400">
              <MessageSquare className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{"It's"} okay to find issues</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Iteration is normal. The goal {"isn't"} perfection on the first pass — {"it's"}
                capturing what needs to happen next.
              </p>
            </div>
          </div>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 3 — Iterate & improve                                                */
/* -------------------------------------------------------------------------- */

export function IterateStep() {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Turn feedback into tasks">
        <p className="text-sm leading-7 text-muted-foreground">
          If you found things that need fixing during review, create new tasks in SpecTree.
          Be specific — instead of {"\"fix the thing\""}, say {"\"the submit button doesn't show a loading state\""}. The more
          specific you are, the better the next run will go.
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <InfoCard
            icon={ClipboardList}
            title="Create follow-up tasks"
            description="Turn your review notes into clear, actionable items inside the epic."
            accentClass="text-rose-400"
          />
          <InfoCard
            icon={Eye}
            title="Add context"
            description={"Attach screenshots, notes, or links so the next pass starts with full context."}
            accentClass="text-rose-400"
          />
        </div>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Re-run if needed">
        <p className="text-sm leading-7 text-muted-foreground">
          You can run the Orchestrator again — on the whole epic or just a single feature.
          Everything it did before is still recorded, so it picks up where things left off.
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <InfoCard
            icon={RefreshCw}
            title="Re-run the epic"
            description="The Orchestrator skips completed features and only works on what's left."
            accentClass="text-rose-400"
          />
          <InfoCard
            icon={Sparkles}
            title="Target a feature"
            description="Focus on just one feature that needs more work without touching the rest."
            accentClass="text-rose-400"
          />
        </div>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title={"That's the whole cycle!"}>
        <p className="text-sm leading-7 text-muted-foreground">
          {"You've"} now seen the complete SpecTree workflow:
        </p>
        <div className="space-y-2">
          <CycleStep icon={Lightbulb} label="Request" description="Share your idea with the team" />
          <CycleStep icon={ClipboardList} label="Plan" description="Planner turns it into a structured epic" />
          <CycleStep icon={Zap} label="Execute" description="Orchestrator does the work" />
          <CycleStep icon={SearchCheck} label="Review" description="You check the output" />
          <CycleStep icon={RotateCw} label="Iterate" description="Refine and repeat as needed" />
        </div>
        <div className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-400">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{"You're"} ready to go!</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Head back to the Help Center anytime you need a refresher on any of these steps.
              </p>
            </div>
          </div>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

/* ── Helpers ── */

function ReviewItem({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-xs font-bold text-rose-400">?</span>
      <p className="text-sm leading-6 text-muted-foreground">{children}</p>
    </div>
  );
}

function CycleStep({ icon: Icon, label, description }: { icon: React.ComponentType<{ className?: string }>; label: string; description: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.06]">
        <Icon className="h-4 w-4 text-foreground/70" />
      </div>
      <div>
        <span className="text-sm font-semibold text-foreground">{label}</span>
        <span className="text-sm text-muted-foreground">{" \u2014 "}{description}</span>
      </div>
    </div>
  );
}
